import {
  linearTiming,
  type TransitionPresentation,
  type TransitionTiming,
} from "@remotion/transitions";
import {
  TransitionDirection,
  TransitionDto,
  TransitionKind,
  TransitionSpeed,
} from "@repo/api/transition";
import type { CSSProperties, FC } from "react";
import { msToFrames } from "../../utils/timing.ts";
import { easeInOutCubic } from "../../utils/easing.ts";
import { ImageFadeTransitionBuilder } from "./ImageFadeTransitionBuilder.ts";
import { ImagePushTransitionBuilder } from "./ImagePushTransitionBuilder.ts";
import { ImageWipeTransitionBuilder } from "./ImageWipeTransitionBuilder.ts";
import { ImageFadeBlackTransitionBuilder } from "./ImageFadeBlackTransitionBuilder.tsx";
import { ImageZoomInOutTransitionBuilder } from "./ImageZoomInOutTransitionBuilder.ts";
import { ImageZoomInFadeTransitionBuilder } from "./ImageZoomInFadeTransitionBuilder.ts";
import { ImageNoneTransitionBuilder } from "./ImageNoneTransitionBuilder.ts";
import { ImageFadeWhiteTransitionBuilder } from "./ImageFadeWhiteTransitionBuilder.tsx";
import { ImageLightLeakTransitionBuilder } from "./ImageLightLeakTransitionBuilder.tsx";
import { ImageZoomBlurTransitionBuilder } from "./ImageZoomBlurTransitionBuilder.ts";
import { ImageZoomOutBlurTransitionBuilder } from "./ImageZoomOutBlurTransitionBuilder.ts";
import { ImageGlitchCutTransitionBuilder } from "./ImageGlitchCutTransitionBuilder.ts";
import { ImageFocusPullTransitionBuilder } from "./ImageFocusPullTransitionBuilder.ts";
import { ImageWhipPanTransitionBuilder } from "./ImageWhipPanTransitionBuilder.ts";
import { ImageClockWipeTransitionBuilder } from "./ImageClockWipeTransitionBuilder.ts";
import { ImageSplitRevealTransitionBuilder } from "./ImageSplitRevealTransitionBuilder.ts";
import { ImageSplitScreenWipeTransitionBuilder } from "./ImageSplitScreenWipeTransitionBuilder.ts";

export interface TransitionFormConfig {
  kind: TransitionKind;
  buildDefaultValue: () => TransitionDto;
  speed?: TransitionSpeed[];
  direction?: TransitionDirection[];
  intensity?: boolean;
  seed?: boolean;
}

export interface ImageTransitionBuilderProps {
  imageTransition: TransitionDto;
  fps: number;
}

export interface ImageTransitionPreviewClipDurations {
  previewClip1DurationInFrames: number;
  previewClip2DurationInFrames: number;
}

export interface ImageTransition<T extends Record<string, unknown>> {
  getDurationInFrames: (props: ImageTransitionBuilderProps) => number;
  buildPresentation: (
    props: ImageTransitionBuilderProps,
  ) => TransitionPresentation<T>;
  buildTiming: (props: ImageTransitionBuilderProps) => TransitionTiming;
}

export interface ImageTransitionWrapperProps extends ImageTransitionBuilderProps {
  imageDurationInFrames: number;
  currentFrame: number;
}

export interface ImageWrapper {
  overlayComponent?: FC<ImageTransitionWrapperProps>;
  buildWrapperStyle?: (
    props: ImageTransitionWrapperProps,
  ) => CSSProperties | null;
}

export interface ImageTransitionWrappers {
  prevImageWrapper?: ImageWrapper;
  nextImageWrapper?: ImageWrapper;
}

export interface ImageTransitionBuilder<T extends Record<string, unknown>> {
  formConfig: TransitionFormConfig;
  transition?: ImageTransition<T>;
  wrappers?: ImageTransitionWrappers;
  getPreviewClipDurationsInFrames: (
    props: ImageTransitionBuilderProps,
  ) => ImageTransitionPreviewClipDurations;
}

export const DEFAULT_TRANSITION_DURATION_MS = new Map([
  ["SLOW", 2000],
  ["NORMAL", 1000],
  ["FAST", 500],
]);

export function getDefaultDurationInFrames(props: ImageTransitionBuilderProps) {
  const speed = props.imageTransition.speed;
  if (!speed) {
    throw new Error(
      "Image transition speed is required when using getDefaultDurationInFrames",
    );
  }

  const duration = DEFAULT_TRANSITION_DURATION_MS.get(speed);
  if (!duration) {
    throw new Error(`Unknown image transition speed: ${speed}`);
  }

  return msToFrames(duration, props.fps);
}

export function getDefaultPreviewClipDurationsInFrames(
  props: ImageTransitionBuilderProps,
): ImageTransitionPreviewClipDurations {
  const speed = props.imageTransition.speed;
  if (!speed) {
    throw new Error(
      "Image transition speed is required when using getDefaultPreviewClipDurationsInFrames",
    );
  }

  if (speed === "SLOW") {
    return {
      previewClip1DurationInFrames: msToFrames(2400, props.fps),
      previewClip2DurationInFrames: msToFrames(2400, props.fps),
    };
  } else if (speed === "NORMAL") {
    return {
      previewClip1DurationInFrames: msToFrames(1600, props.fps),
      previewClip2DurationInFrames: msToFrames(2000, props.fps),
    };
  } else if (speed === "FAST") {
    return {
      previewClip1DurationInFrames: msToFrames(1400, props.fps),
      previewClip2DurationInFrames: msToFrames(1800, props.fps),
    };
  } else {
    throw new Error(`Unknown image transition speed: ${speed}`);
  }
}

export function getDefaultTiming(props: ImageTransitionBuilderProps) {
  return linearTiming({
    durationInFrames: getDefaultDurationInFrames(props),
    easing: easeInOutCubic,
  });
}

export const ImageTransitionBuilders = new Map([
  [TransitionKind.enum.NONE, ImageNoneTransitionBuilder],
  [TransitionKind.enum.FADE, ImageFadeTransitionBuilder],
  [TransitionKind.enum.FADE_BLACK, ImageFadeBlackTransitionBuilder],
  [TransitionKind.enum.FADE_WHITE, ImageFadeWhiteTransitionBuilder],
  [TransitionKind.enum.PUSH, ImagePushTransitionBuilder],
  [TransitionKind.enum.WIPE, ImageWipeTransitionBuilder],
  [TransitionKind.enum.ZOOM_IN_FADE, ImageZoomInFadeTransitionBuilder],
  [TransitionKind.enum.ZOOM_IN_OUT, ImageZoomInOutTransitionBuilder],
  [TransitionKind.enum.ZOOM_BLUR, ImageZoomBlurTransitionBuilder],
  [TransitionKind.enum.ZOOM_OUT_BLUR, ImageZoomOutBlurTransitionBuilder],
  [TransitionKind.enum.GLITCH_CUT, ImageGlitchCutTransitionBuilder],
  [TransitionKind.enum.FOCUS_PULL, ImageFocusPullTransitionBuilder],
  [TransitionKind.enum.WHIP_PAN, ImageWhipPanTransitionBuilder],
  [TransitionKind.enum.CLOCK_WIPE, ImageClockWipeTransitionBuilder],
  [TransitionKind.enum.SPLIT_REVEAL, ImageSplitRevealTransitionBuilder],
  [
    TransitionKind.enum.SPLIT_SCREEN_WIPE,
    ImageSplitScreenWipeTransitionBuilder,
  ],
  [TransitionKind.enum.LIGHT_LEAK, ImageLightLeakTransitionBuilder],
]);
